/**
 * GameEngine.ts - Main game engine class for DEFCON TERMINAL
 *
 * Orchestrates all game systems: state machine, decision tree, state management,
 * scoring, and save/load functionality. Decoupled from UI via event system.
 */

import type {
  Scenario,
  ScenarioNode,
  ChoiceOption,
  EndingNode,
  PathType,
  OutcomeType,
  GameState,
  Decision,
  ScoreBreakdown,
  CompletedScenario,
  ScenarioState,
  RuntimeGameState,
} from '@shared/types';
import { EventEmitter, type EventMap } from './EventEmitter';
import { StateMachine, GameStateType } from './StateMachine';
import { StateManager } from './StateManager';
import { DecisionTree, type NodeVisit, type TreeStats } from './DecisionTree';
import { ScenarioParser, type ParseResult } from './ScenarioParser';
import { ScoreCalculator, type CalculationResult } from './ScoreCalculator';
import { SaveManager, type SaveData, type SaveResult } from './SaveManager';

/**
 * Engine events
 */
export interface GameEngineEvents extends EventMap {
  // Scenario lifecycle
  'scenario:load': { scenarioId: string; scenario: Scenario };
  'scenario:start': { scenarioId: string; startNode: string };
  'scenario:end': {
    scenarioId: string;
    ending: EndingNode;
    score: ScoreBreakdown;
    stats: TreeStats;
  };

  // Node navigation
  'node:change': {
    previousNode: string | null;
    currentNode: string;
    node: ScenarioNode;
  };
  'node:narrative': { nodeId: string; content: string[]; ascii?: string };
  'node:choice': {
    nodeId: string;
    prompt: string;
    options: ChoiceOption[];
    timeLimit?: number;
  };
  'node:ending': {
    nodeId: string;
    ending: EndingNode;
    score: ScoreBreakdown;
  };

  // Player actions
  'choice:made': {
    nodeId: string;
    choiceId: string;
    option: ChoiceOption;
    stateChanges: ScenarioState;
  };
  'choice:timeout': {
    nodeId: string;
    defaultChoiceId: string;
  };

  // State changes
  'state:update': { previous: ScenarioState; current: ScenarioState };
  'defcon:change': { previous: number; current: number };
  'gamestate:change': { from: GameStateType; to: GameStateType };

  // Save/Load
  'save:complete': SaveResult;
  'load:complete': { success: boolean; data?: SaveData };

  // Errors
  'error': { code: string; message: string; details?: unknown };
}

/**
 * Engine configuration
 */
export interface GameEngineConfig {
  apiBaseUrl?: string;
  sessionToken?: string;
  autoSave?: boolean;
  autoSaveInterval?: number;
}

/**
 * Scenario completion payload
 */
export interface CompletionPayload {
  scenarioId: string;
  decisions: Decision[];
  playTimeSeconds: number;
  outcomeType: OutcomeType;
  path: PathType;
  casualties?: number;
  finalDefcon?: number;
}

/**
 * Scenario completion response from reporter
 */
export interface CompletionReporterResult {
  score: number;
  scoreBreakdown: ScoreBreakdown;
  rank: number;
  achievements?: string[];
}

/**
 * Scenario completion result for UI
 */
export interface CompletionResult {
  score: number;
  breakdown: ScoreBreakdown;
  rank: number;
  achievements: string[];
}

/**
 * Scenario loader function type
 */
export type ScenarioLoader = (scenarioId: string) => Promise<Scenario | null>;

/**
 * GameEngine class - Main orchestrator for the game
 */
export class GameEngine extends EventEmitter<GameEngineEvents> {
  // Sub-systems
  private stateMachine: StateMachine;
  private stateManager: StateManager;
  private decisionTree: DecisionTree;
  private scenarioParser: ScenarioParser;
  private scoreCalculator: ScoreCalculator;
  private saveManager: SaveManager;

  // Current state
  private currentScenario: Scenario | null = null;
  private scenarioLoader: ScenarioLoader | null = null;
  private gameState: GameState;
  private startTime: number | null = null;
  private isInitialized: boolean = false;
  // Snapshot of state at each node visit for accurate decision history
  private stateAtVisit: Map<string, object> = new Map();
  private lastCompletion:
    | { ending: EndingNode; result: CalculationResult; playTimeSeconds: number }
    | null = null;
  private completionReporter:
    | ((payload: CompletionPayload) => Promise<CompletionReporterResult | null>)
    | null = null;

  constructor(config?: GameEngineConfig) {
    super();

    // Initialize sub-systems
    this.stateMachine = new StateMachine(undefined, {
      persistToStorage: true,
    });

    this.stateManager = new StateManager();
    this.decisionTree = new DecisionTree();
    this.scenarioParser = new ScenarioParser();
    this.scoreCalculator = new ScoreCalculator();

    this.saveManager = new SaveManager(
      config?.apiBaseUrl
        ? {
            baseUrl: config.apiBaseUrl,
            sessionToken: config.sessionToken,
          }
        : undefined
    );

    // Initialize game state
    this.gameState = this.createEmptyGameState();

    // Wire up internal events
    this.setupInternalEvents();
  }

  /**
   * Create empty game state
   */
  private createEmptyGameState(): GameState {
    return {
      currentPath: null,
      currentScenario: null,
      currentNode: null,
      decisions: [],
      completedScenarios: [],
      completedScenarioDetails: [],
      discoveredEasterEggs: [],
      totalPlayTime: 0,
      scenarioStartTime: null,
    };
  }

  /**
   * Set up internal event forwarding
   */
  private setupInternalEvents(): void {
    // Forward state machine events
    this.stateMachine.on('state:transition', ({ from, to }) => {
      this.emit('gamestate:change', { from, to });
    });

    // Forward state manager events
    this.stateManager.on('state:change', ({ previous, current }) => {
      this.emit('state:update', { previous, current });
    });

    this.stateManager.on('defcon:change', ({ previous, current }) => {
      this.emit('defcon:change', { previous, current });
    });

    // Forward save manager events
    this.saveManager.on('save:complete', (result) => {
      this.emit('save:complete', result);
    });

    this.saveManager.on('load:complete', (result) => {
      this.emit('load:complete', result);
    });
  }

  /**
   * Initialize the game engine
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // Try to restore state from storage
    const loadResult = await this.saveManager.loadFromLocal();
    if (loadResult.success && loadResult.data) {
      this.restoreFromSaveData(loadResult.data);
    }

    // Restore state machine state
    this.stateMachine.loadFromStorage();

    this.isInitialized = true;
  }

  /**
   * Set the scenario loader function
   */
  setScenarioLoader(loader: ScenarioLoader): void {
    this.scenarioLoader = loader;
  }

  /**
   * Set session token for API calls
   */
  setSessionToken(token: string): void {
    this.saveManager.setSessionToken(token);
  }

  /**
   * Set completion reporter for server submissions
   */
  setCompletionReporter(
    reporter: (payload: CompletionPayload) => Promise<CompletionReporterResult | null>
  ): void {
    this.completionReporter = reporter;
  }

  // ========================================
  // State Machine Methods
  // ========================================

  /**
   * Get current game state type
   */
  getGameStateType(): GameStateType {
    return this.stateMachine.getState();
  }

  /**
   * Check if a scenario is actively playing
   */
  isPlaying(): boolean {
    return !!this.currentScenario && !this.decisionTree.isAtEnding();
  }

  /**
   * Transition to a new game state
   */
  async transitionTo(state: GameStateType): Promise<boolean> {
    return this.stateMachine.transitionTo(state);
  }

  /**
   * Check if can transition to state
   */
  canTransitionTo(state: GameStateType): boolean {
    return this.stateMachine.canTransitionTo(state);
  }

  // ========================================
  // Scenario Methods
  // ========================================

  /**
   * Load a scenario by ID
   */
  async loadScenario(scenarioId: string): Promise<ParseResult> {
    if (!this.scenarioLoader) {
      return {
        success: false,
        errors: [{ code: 'NO_LOADER', message: 'No scenario loader configured' }],
        warnings: [],
      };
    }

    try {
      const scenario = await this.scenarioLoader(scenarioId);

      if (!scenario) {
        return {
          success: false,
          errors: [
            {
              code: 'NOT_FOUND',
              message: `Scenario "${scenarioId}" not found`,
            },
          ],
          warnings: [],
        };
      }

      // Validate scenario
      const parseResult = this.scenarioParser.parse(scenario);

      if (!parseResult.success) {
        this.emit('error', {
          code: 'PARSE_ERROR',
          message: 'Scenario validation failed',
          details: parseResult.errors,
        });
        return parseResult;
      }

      // Set up scenario
      this.currentScenario = parseResult.scenario!;
      this.lastCompletion = null;
      this.scoreCalculator.setScenario(this.currentScenario);
      this.stateManager.initialize(this.currentScenario.initialState);
      this.decisionTree.initialize(this.currentScenario, this.stateManager);

      // Update game state
      this.gameState.currentScenario = scenarioId;
      this.gameState.currentPath = this.currentScenario.path;
      this.gameState.currentNode = this.currentScenario.startNode;

      this.emit('scenario:load', {
        scenarioId,
        scenario: this.currentScenario,
      });

      return parseResult;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit('error', {
        code: 'LOAD_ERROR',
        message: `Failed to load scenario: ${message}`,
      });

      return {
        success: false,
        errors: [{ code: 'LOAD_ERROR', message }],
        warnings: [],
      };
    }
  }

  /**
   * Start the loaded scenario
   */
  start(): boolean {
    if (!this.currentScenario) {
      this.emit('error', {
        code: 'NO_SCENARIO',
        message: 'No scenario loaded',
      });
      return false;
    }

    // Record start time
    this.startTime = Date.now();
    this.gameState.scenarioStartTime = this.startTime;

    // Emit start event
    this.emit('scenario:start', {
      scenarioId: this.currentScenario.id,
      startNode: this.currentScenario.startNode,
    });

    // Navigate to start node
    const startNode = this.getCurrentNode();
    if (startNode) {
      this.emitNodeEvent(this.currentScenario.startNode, startNode);
    }

    return true;
  }

  /**
   * Make a choice at the current node
   */
  makeChoice(optionId: string): ScenarioNode | null {
    if (!this.currentScenario) {
      this.emit('error', {
        code: 'NO_SCENARIO',
        message: 'No scenario loaded',
      });
      return null;
    }

    const currentNodeId = this.decisionTree.getCurrentNodeId();
    const currentNode = this.decisionTree.getCurrentNode();

    if (!currentNode || !currentNodeId) {
      return null;
    }

    // Find the chosen option
    let chosenOption: ChoiceOption | undefined;
    if (
      currentNode.type === 'choice' ||
      currentNode.type === 'multi-select' ||
      currentNode.type === 'timed'
    ) {
      chosenOption = currentNode.options.find((opt) => opt.id === optionId);
    }

    if (!chosenOption) {
      this.emit('error', {
        code: 'INVALID_CHOICE',
        message: `Option "${optionId}" not found`,
      });
      return null;
    }

    // Apply state changes if any
    if (chosenOption.stateChanges) {
      this.stateManager.applyChanges(chosenOption.stateChanges);
    }

    // Record decision
    const decision: Decision = {
      scenarioId: this.currentScenario.id,
      nodeId: currentNodeId,
      choice: optionId,
      timestamp: Date.now(),
    };
    this.gameState.decisions.push(decision);

    // Navigate to next node
    const nextNode = this.decisionTree.navigateToChoice(optionId);

    // Emit choice event
    this.emit('choice:made', {
      nodeId: currentNodeId,
      choiceId: optionId,
      option: chosenOption,
      stateChanges: this.stateManager.getState(),
    });

    // Update game state
    this.gameState.currentNode = this.decisionTree.getCurrentNodeId();

    // Mark save as dirty
    this.saveManager.markDirty();

    // Emit node event for the new node
    if (nextNode) {
      const nextNodeId = this.decisionTree.getCurrentNodeId()!;
      this.emitNodeEvent(nextNodeId, nextNode);

      // Check if we reached an ending
      if (nextNode.type === 'ending') {
        this.handleEnding(nextNodeId, nextNode as EndingNode);
      }
    }

    return nextNode;
  }

  /**
   * Handle timeout for timed choices
   */
  handleTimeout(): ScenarioNode | null {
    const currentNode = this.getCurrentNode();

    if (!currentNode || currentNode.type !== 'timed') {
      return null;
    }

    const currentNodeId = this.decisionTree.getCurrentNodeId()!;
    const defaultOptionId = currentNode.defaultOption;

    this.emit('choice:timeout', {
      nodeId: currentNodeId,
      defaultChoiceId: defaultOptionId,
    });

    return this.makeChoice(defaultOptionId);
  }

  /**
   * Advance through narrative/consequence nodes
   */
  advance(): ScenarioNode | null {
    const nextNode = this.decisionTree.advance();

    if (nextNode) {
      const nextNodeId = this.decisionTree.getCurrentNodeId()!;
      this.gameState.currentNode = nextNodeId;
      this.emitNodeEvent(nextNodeId, nextNode);

      if (nextNode.type === 'ending') {
        this.handleEnding(nextNodeId, nextNode as EndingNode);
      }
    }

    return nextNode;
  }

  /**
   * Handle reaching an ending
   */
  private handleEnding(nodeId: string, ending: EndingNode): void {
    if (!this.currentScenario) {
      return;
    }

    // Calculate play time
    const playTimeSeconds = this.startTime
      ? Math.floor((Date.now() - this.startTime) / 1000)
      : 0;

    // Calculate score
    const calculationResult = this.scoreCalculator.calculate(
      this.decisionTree,
      ending,
      {
        includeEasterEggs: true,
        includeSpeedBonus: true,
        playTimeSeconds,
        casualties: ending.casualties,
        warheadsUsed: ending.warheadsUsed,
        discoveredEasterEggs: this.gameState.discoveredEasterEggs,
      }
    );

    // Store completion details for UI and reporting
    this.lastCompletion = {
      ending,
      result: calculationResult,
      playTimeSeconds,
    };

    // Create completed scenario record
    const completedScenario: CompletedScenario = {
      scenarioId: this.currentScenario.id,
      path: this.currentScenario.path,
      outcomeType: ending.outcomeType,
      score: calculationResult.breakdown.total,
      scoreBreakdown: calculationResult.breakdown,
      decisionsCount: this.decisionTree.getDecisionCount(),
      playTimeSeconds,
      completedAt: Date.now(),
      casualties: ending.casualties,
      warheadsUsed: ending.warheadsUsed,
    };

    // Update game state - store both ID (for backward compat) and full details
    if (!this.gameState.completedScenarios.includes(this.currentScenario.id)) {
      this.gameState.completedScenarios.push(this.currentScenario.id);
    }
    if (!this.gameState.completedScenarioDetails) {
      this.gameState.completedScenarioDetails = [];
    }
    this.gameState.completedScenarioDetails.push(completedScenario);
    this.gameState.totalPlayTime += playTimeSeconds;

    // Emit events
    this.emit('node:ending', {
      nodeId,
      ending,
      score: calculationResult.breakdown,
    });

    this.emit('scenario:end', {
      scenarioId: this.currentScenario.id,
      ending,
      score: calculationResult.breakdown,
      stats: calculationResult.stats,
    });

    // Save game
    this.save();
  }

  /**
   * Complete scenario for UI (optionally reporting to server)
   */
  async completeScenario(): Promise<CompletionResult> {
    if (!this.currentScenario) {
      throw new Error('No scenario loaded');
    }

    const scenarioId = this.currentScenario.id;
    const endingNode =
      this.lastCompletion?.ending ||
      (this.getCurrentNode()?.type === 'ending'
        ? (this.getCurrentNode() as EndingNode)
        : null);

    if (!endingNode) {
      throw new Error('No ending reached');
    }

    const playTimeSeconds =
      this.lastCompletion?.playTimeSeconds ??
      (this.startTime ? Math.floor((Date.now() - this.startTime) / 1000) : 0);

    const localResult =
      this.lastCompletion?.result ??
      this.scoreCalculator.calculate(this.decisionTree, endingNode, {
        includeEasterEggs: true,
        includeSpeedBonus: true,
        playTimeSeconds,
        casualties: endingNode.casualties,
        warheadsUsed: endingNode.warheadsUsed,
        discoveredEasterEggs: this.gameState.discoveredEasterEggs,
      });

    const fallback: CompletionResult = {
      score: localResult.breakdown.total,
      breakdown: localResult.breakdown,
      rank: 0,
      achievements: [],
    };

    if (!this.completionReporter) {
      return fallback;
    }

    try {
      const decisions = this.gameState.decisions.filter(
        (decision) => decision.scenarioId === scenarioId
      );
      const response = await this.completionReporter({
        scenarioId,
        decisions,
        playTimeSeconds,
        outcomeType: endingNode.outcomeType,
        path: this.currentScenario.path,
        casualties: endingNode.casualties,
      });

      if (!response) {
        return fallback;
      }

      return {
        score: response.score ?? fallback.score,
        breakdown: response.scoreBreakdown ?? fallback.breakdown,
        rank: response.rank ?? fallback.rank,
        achievements: response.achievements ?? [],
      };
    } catch (error) {
      console.warn('Completion reporter failed:', error);
      return fallback;
    }
  }

  /**
   * Emit appropriate event for a node
   */
  private emitNodeEvent(nodeId: string, node: ScenarioNode): void {
    const previousNodeId = this.gameState.currentNode;

    // Snapshot current state at this visit for accurate history replay
    this.stateAtVisit.set(
      nodeId,
      JSON.parse(JSON.stringify(this.stateManager.getState()))
    );

    this.emit('node:change', {
      previousNode: previousNodeId,
      currentNode: nodeId,
      node,
    });

    switch (node.type) {
      case 'narrative':
      case 'consequence':
        this.emit('node:narrative', {
          nodeId,
          content: node.content,
          ascii: node.ascii,
        });
        break;

      case 'choice':
      case 'multi-select':
        this.emit('node:choice', {
          nodeId,
          prompt: node.prompt,
          options: this.decisionTree.getAvailableOptions(),
          timeLimit: undefined,
        });
        break;

      case 'timed':
        this.emit('node:choice', {
          nodeId,
          prompt: node.prompt,
          options: this.decisionTree.getAvailableOptions(),
          timeLimit: node.timeLimit,
        });
        break;

      case 'ending':
        // Handled separately in handleEnding
        break;
    }
  }

  // ========================================
  // State Access Methods
  // ========================================

  /**
   * Get current scenario
   */
  getScenario(): Scenario | null {
    return this.currentScenario;
  }

  /**
   * Get current node
   */
  getCurrentNode(): ScenarioNode | null {
    return this.decisionTree.getCurrentNode();
  }

  /**
   * Get current node ID
   */
  getCurrentNodeId(): string | null {
    return this.decisionTree.getCurrentNodeId();
  }

  /**
   * Get current scenario state
   */
  getScenarioState(): ScenarioState | null {
    if (!this.currentScenario) {
      return null;
    }
    return this.stateManager.getState();
  }

  /**
   * Get game state
   */
  getState(): GameState {
    return { ...this.gameState };
  }

  /**
   * Get runtime game state
   */
  getRuntimeState(): RuntimeGameState {
    return {
      isPlaying: this.stateMachine.isInState(GameStateType.PLAYING),
      currentScenario: this.gameState.currentScenario,
      currentNode: this.gameState.currentNode,
      scenarioState: this.stateManager.getState(),
      decisionHistory: this.decisionTree.getVisitHistory().map((v) => ({
        nodeId: v.nodeId,
        choiceId: v.choiceId ?? '',
        timestamp: v.timestamp,
        stateSnapshot: (this.stateAtVisit.get(v.nodeId) as ScenarioState) || this.stateManager.getState(),
      })),
      startTime: this.startTime,
      lastSaveTime: this.saveManager.getLastSaveTime(),
    };
  }

  /**
   * Get available options at current node
   */
  getAvailableOptions(): ChoiceOption[] {
    return this.decisionTree.getAvailableOptions();
  }

  /**
   * Get decision tree statistics
   */
  getStats(): TreeStats {
    return this.decisionTree.getStats();
  }

  /**
   * Check if at an ending
   */
  isAtEnding(): boolean {
    return this.decisionTree.isAtEnding();
  }

  /**
   * Check if current node requires a choice
   */
  requiresChoice(): boolean {
    return this.decisionTree.requiresChoice();
  }

  // ========================================
  // Save/Load Methods
  // ========================================

  /**
   * Save game state
   */
  async save(): Promise<SaveResult> {
    const saveData = this.saveManager.createSaveData(
      this.gameState,
      this.stateManager.getState(),
      this.decisionTree.getVisitHistory().map((v) => ({
        nodeId: v.nodeId,
        choiceId: v.choiceId ?? '',
        timestamp: v.timestamp,
        stateSnapshot: this.stateManager.getState(),
      }))
    );

    return this.saveManager.save(saveData);
  }

  /**
   * Load game state
   */
  async load(): Promise<boolean> {
    const result = await this.saveManager.load();

    if (result.success && result.data) {
      return this.restoreFromSaveData(result.data);
    }

    return false;
  }

  /**
   * Restore state from save data
   */
  private restoreFromSaveData(data: SaveData): boolean {
    try {
      // Restore game state
      this.gameState = {
        ...this.createEmptyGameState(),
        ...data.gameState,
      };

      // Restore scenario state if present
      if (data.scenarioState) {
        this.stateManager.fromJSON(data.scenarioState);
      }

      // If there's a current scenario, we need to reload it
      if (this.gameState.currentScenario && this.scenarioLoader) {
        // This would need to be async - for now just mark that we have saved state
        this.startTime = data.gameState.scenarioStartTime;
      }

      return true;
    } catch (error) {
      this.emit('error', {
        code: 'RESTORE_ERROR',
        message: 'Failed to restore save data',
        details: error,
      });
      return false;
    }
  }

  /**
   * Check if there's a save to continue
   */
  hasSave(): boolean {
    return this.saveManager.hasSave();
  }

  /**
   * Delete saved game
   */
  async deleteSave(): Promise<boolean> {
    return this.saveManager.deleteSave();
  }

  // ========================================
  // Utility Methods
  // ========================================

  /**
   * Reset the engine to initial state
   */
  reset(): void {
    this.currentScenario = null;
    this.startTime = null;
    this.lastCompletion = null;
    this.stateAtVisit.clear();
    this.gameState = this.createEmptyGameState();
    this.stateManager.reset();
    this.decisionTree.reset();
    this.stateMachine.reset();
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.saveManager.stopAutoSave();
    this.removeAllListeners();
    this.stateMachine.removeAllListeners();
    this.stateManager.removeAllListeners();
    this.decisionTree.removeAllListeners();
  }

  /**
   * Get the score calculator for external use
   */
  getScoreCalculator(): ScoreCalculator {
    return this.scoreCalculator;
  }

  /**
   * Get the state manager for external use
   */
  getStateManager(): StateManager {
    return this.stateManager;
  }

  /**
   * Get the decision tree for external use
   */
  getDecisionTree(): DecisionTree {
    return this.decisionTree;
  }

  /**
   * Start auto-save
   */
  startAutoSave(interval?: number): void {
    this.saveManager.startAutoSave(
      () =>
        this.saveManager.createSaveData(
          this.gameState,
          this.stateManager.getState(),
          this.decisionTree.getVisitHistory().map((v) => ({
            nodeId: v.nodeId,
            choiceId: v.choiceId ?? '',
            timestamp: v.timestamp,
            stateSnapshot: this.stateManager.getState(),
          }))
        ),
      interval
    );
  }

  /**
   * Stop auto-save
   */
  stopAutoSave(): void {
    this.saveManager.stopAutoSave();
  }
}

/**
 * Create a new game engine instance
 */
export function createGameEngine(config?: GameEngineConfig): GameEngine {
  return new GameEngine(config);
}
